import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Send, ChevronDown, Sparkles, User, Bot, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { SunIcon, MoonIcon, VideoIcon, DownloadIcon } from '../components/Icons';
import VideoModal from '../components/VideoModal';
import { getThemeClasses } from '../components/theme';

const WS_BASE = process.env.REACT_APP_WS_URL || `ws://${window.location.host}`;
const API_BASE = process.env.REACT_APP_API_URL || `http://${window.location.host}`;

export default function VisitorPage() {
    const { slug } = useParams();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [connected, setConnected] = useState(false);
    const [presenting, setPresenting] = useState(false);
    const [typing, setTyping] = useState(false);
    const [recruiterInfo, setRecruiterInfo] = useState(null);
    const [personaInfo, setPersonaInfo] = useState(null);
    const [error, setError] = useState(null);
    const [sessionToken, setSessionToken] = useState(null);
    const [showScrollBtn, setShowScrollBtn] = useState(false);
    const [imageModal, setImageModal] = useState({ open: false, src: '', caption: '' });
    const [darkMode, setDarkMode] = useState(true);
    const [waitingForResponse, setWaitingForResponse] = useState(false);
    const [introVideo, setIntroVideo] = useState(null);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [attachedPdf, setAttachedPdf] = useState(null);
    const [presentationStarted, setPresentationStarted] = useState(false);
    const [wsReady, setWsReady] = useState(false);

    const t = getThemeClasses(darkMode);

    const wsRef = useRef(null);
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = useCallback((force = false) => {
        if (force || !showScrollBtn) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [showScrollBtn]);

    // Handle scroll position
    const handleScroll = () => {
        const el = chatContainerRef.current;
        if (!el) return;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
        setShowScrollBtn(!atBottom);
    };

    // 1. Init session via REST API to get session token
    useEffect(() => {
        const abortController = new AbortController();
        let cancelled = false;

        const initSession = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/v1/chat/init/${slug}`, {
                    signal: abortController.signal,
                });
                if (cancelled) return;
                if (!res.ok) throw new Error('Init failed');
                const data = await res.json();
                if (cancelled) return;
                setSessionToken(data.session_token);
                setRecruiterInfo({
                    name: data.recruiter_name,
                    company: data.company_name,
                });
                setPersonaInfo({
                    name: data.persona_name,
                    avatar: data.persona_avatar_url,
                });
                // Check for intro video
                if (data.intro_video_url) {
                    const videoUrl = data.intro_video_url.startsWith('http')
                        ? data.intro_video_url
                        : API_BASE + data.intro_video_url;
                    setIntroVideo(videoUrl);
                    const videoSeenKey = `talktomyai_video_seen_${slug}`;
                    if (!localStorage.getItem(videoSeenKey)) {
                        setShowVideoModal(true);
                        // wsReady stays false — WS waits for video dismiss
                    } else {
                        setWsReady(true);
                    }
                } else {
                    setWsReady(true);
                }
                // Check for attached PDF
                if (data.attached_pdf_url) {
                    const pdfUrl = data.attached_pdf_url.startsWith('http')
                        ? data.attached_pdf_url
                        : API_BASE + data.attached_pdf_url;
                    setAttachedPdf(pdfUrl);
                }
            } catch (e) {
                if (cancelled || e.name === 'AbortError') return;
                setError('Lien invalide ou expiré.');
            }
        };
        initSession();

        return () => {
            cancelled = true;
            abortController.abort();
        };
    }, [slug]);

    // 2. Connect WebSocket — only after wsReady (video dismissed or no video)
    useEffect(() => {
        if (!sessionToken || !wsReady) return;

        const ws = new WebSocket(`${WS_BASE}/api/v1/chat/ws/${sessionToken}`);
        wsRef.current = ws;

        ws.onopen = () => {
            setConnected(true);
            setError(null);
            setPresentationStarted(true);
        };

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                handleWsMessage(msg);
            } catch (e) {
                console.error('WS parse error:', e);
            }
        };

        ws.onerror = () => {
            setError('Erreur de connexion. Veuillez rafraîchir la page.');
        };

        ws.onclose = () => {
            setConnected(false);
        };

        return () => {
            ws.close();
        };
    }, [sessionToken, wsReady]); // eslint-disable-line

    // Auto-scroll on new messages
    useEffect(() => {
        scrollToBottom();
    }, [messages, typing, scrollToBottom]);

    const handleWsMessage = (msg) => {
        const payload = msg.data || {};

        switch (msg.type) {
            case 'message':
                setMessages((prev) => [...prev, {
                    role: payload.role || 'assistant',
                    content: payload.content,
                    timestamp: new Date(),
                }]);
                setTyping(false);
                break;

            case 'typing':
                setTyping(payload.is_typing !== false);
                break;

            case 'commands':
                // Attach commands to the last assistant message
                setMessages((prev) => {
                    if (prev.length === 0) return prev;
                    const updated = [...prev];
                    const last = { ...updated[updated.length - 1] };
                    last.special_commands = Array.isArray(payload) ? payload : [];
                    updated[updated.length - 1] = last;
                    return updated;
                });
                break;

            case 'step_info':
                setPresenting(true);
                break;

            case 'waiting_for_response':
                setWaitingForResponse(payload.waiting);
                break;

            case 'presentation_complete':
                setPresenting(false);
                setWaitingForResponse(false);
                setMessages((prev) => [...prev, {
                    role: 'system',
                    content: payload.message || 'Présentation terminée ! Vous pouvez maintenant poser vos questions.',
                    timestamp: new Date(),
                }]);
                break;

            case 'error':
                setMessages((prev) => [...prev, {
                    role: 'system',
                    content: payload.message || 'Une erreur est survenue.',
                    timestamp: new Date(),
                    isError: true,
                }]);
                setTyping(false);
                break;

            default:
                // Ignore unrecognized message types to avoid duplicates
                break;
        }
    };

    const sendMessage = () => {
        const text = input.trim();
        if (!text || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

        // Add user message to chat
        setMessages((prev) => [...prev, {
            role: 'user',
            content: text,
            timestamp: new Date(),
        }]);

        // Send to WebSocket
        wsRef.current.send(JSON.stringify({ type: 'message', data: { content: text } }));
        setInput('');
        setTyping(true);
        setWaitingForResponse(false);
        inputRef.current?.focus();
    };

    const handleSuggestionClick = (text) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        setMessages((prev) => [...prev, {
            role: 'user',
            content: text,
            timestamp: new Date(),
        }]);
        // Send continue with chosen option to resume presentation
        wsRef.current.send(JSON.stringify({ type: 'continue', data: { content: text } }));
        setWaitingForResponse(false);
        setTyping(true);
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Render special commands (images, skills, etc.)
    const renderSpecialContent = (commands) => {
        if (!commands || commands.length === 0) return null;
        return commands.map((cmd, i) => {
            if (cmd.type === 'show_image') {
                const imgSrc = cmd.image_url
                    ? (cmd.image_url.startsWith('http') ? cmd.image_url : API_BASE + cmd.image_url)
                    : '';
                return (
                    <div key={i} className="mt-3">
                        <img src={imgSrc} alt={cmd.caption || 'Projet'}
                            className="max-w-full rounded-lg shadow-md max-h-64 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setImageModal({ open: true, src: imgSrc, caption: cmd.caption || 'Projet' })} />
                        {cmd.caption && <p className={`text-xs ${t.caption} mt-1`}>{cmd.caption}</p>}
                    </div>
                );
            }
            if (cmd.type === 'show_skills') {
                return (
                    <div key={i} className="mt-3 flex flex-wrap gap-1.5">
                        {(cmd.skills || []).map((skill, j) => (
                            <span key={j} className={`text-xs ${t.skillBadge} px-2 py-1 rounded-full`}>
                                {typeof skill === 'object' ? (skill.name || skill.skill || JSON.stringify(skill)) : skill}
                            </span>
                        ))}
                    </div>
                );
            }
            if (cmd.type === 'show_contact') {
                return (
                    <div key={i} className={`mt-3 p-3 ${t.contactCard} rounded-lg`}>
                        <p className="text-sm font-medium mb-1">📧 Contact</p>
                        {cmd.email && <p className="text-xs">✉️ {cmd.email}</p>}
                        {cmd.phone && <p className="text-xs">📱 {cmd.phone}</p>}
                        {cmd.linkedin && (
                            <a href={cmd.linkedin} target="_blank" rel="noopener noreferrer"
                                className="text-xs underline">🔗 LinkedIn</a>
                        )}
                        {cmd.github && (
                            <a href={cmd.github} target="_blank" rel="noopener noreferrer"
                                className="text-xs underline ml-2">💻 GitHub</a>
                        )}
                    </div>
                );
            }
            if (cmd.type === 'show_suggestions') {
                return (
                    <div key={i} className="mt-3 flex flex-wrap gap-2">
                        {(cmd.options || []).map((option, j) => (
                            <button key={j} onClick={() => handleSuggestionClick(option)}
                                className={`text-xs ${t.suggestionBtn} px-3 py-2 rounded-lg border transition-colors cursor-pointer`}>
                                {option}
                            </button>
                        ))}
                    </div>
                );
            }
            return null;
        });
    };

    return (
        <div className={`h-[100dvh] flex flex-col ${t.bg}`}>
            {/* Header */}
            <header className={`flex-shrink-0 px-4 py-3 ${t.header}`}>
                <div className="max-w-3xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#5764F4] to-purple-500 flex items-center justify-center overflow-hidden">
                            {personaInfo?.avatar ? (
                                <img src={personaInfo.avatar} alt={personaInfo.name} className="w-full h-full object-cover" />
                            ) : (
                                <Sparkles className="w-4 h-4 text-white" />
                            )}
                        </div>
                        <div>
                            <h1 className="text-sm font-semibold">{personaInfo?.name || 'Claud-IA Touré'}</h1>
                            <p className={`text-xs ${t.status}`}>
                                {connected ? (
                                    <span className="flex items-center gap-1">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                                        En ligne
                                    </span>
                                ) : (
                                    'Déconnecté'
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {attachedPdf && (
                            <a href={attachedPdf} target="_blank" rel="noopener noreferrer" download
                                className={`p-2 rounded-full transition-colors ${t.themeBtn}`}
                                title="Télécharger le document PDF">
                                <DownloadIcon />
                            </a>
                        )}
                        {introVideo && presentationStarted && (
                            <button onClick={() => setShowVideoModal(true)}
                                className={`p-2 rounded-full transition-colors ${t.videoBtn}`}
                                title="Revoir la vidéo d'introduction">
                                <VideoIcon />
                            </button>
                        )}
                        <button onClick={() => setDarkMode(!darkMode)}
                            className={`p-2 rounded-full transition-colors ${t.themeBtn}`}
                            title={darkMode ? 'Mode clair' : 'Mode sombre'}>
                            {darkMode ? <SunIcon /> : <MoonIcon />}
                        </button>
                    </div>
                </div>
            </header>

            {/* Messages */}
            <div ref={chatContainerRef} onScroll={handleScroll}
                className="flex-1 overflow-y-auto px-4 py-6">
                <div className="max-w-3xl mx-auto space-y-4">

                    {/* Welcome if no messages */}
                    {messages.length === 0 && !typing && (
                        <div className="text-center py-16">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#5764F4] to-purple-500 flex items-center justify-center overflow-hidden">
                                {personaInfo?.avatar ? (
                                    <img src={personaInfo.avatar} alt={personaInfo?.name} className="w-full h-full object-cover" />
                                ) : (
                                    <Sparkles className="w-7 h-7 text-white" />
                                )}
                            </div>
                            <h2 className="text-xl font-semibold mb-2">Bienvenue !</h2>
                            <p className={`${t.welcomeText} text-sm max-w-md mx-auto`}>
                                {recruiterInfo
                                    ? `Bonjour ${recruiterInfo.name}, je suis l'assistante IA d'Abdoulaye. La présentation va commencer...`
                                    : 'Je suis Claud-IA Touré, l\'assistante IA d\'Abdoulaye Diallo. Connexion en cours...'}
                            </p>
                        </div>
                    )}

                    {messages.map((msg, i) => {
                        if (msg.role === 'system') {
                            return (
                                <div key={i} className="flex justify-center">
                                    <div className={`text-xs px-4 py-2 rounded-full ${msg.isError ? t.systemErr : t.systemBg
                                        }`}>
                                        {msg.content}
                                    </div>
                                </div>
                            );
                        }

                        const isUser = msg.role === 'user';
                        return (
                            <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                                <div className={`flex items-start gap-2.5 max-w-[85%] ${isUser ? 'flex-row-reverse' : ''}`}>
                                    {/* Avatar */}
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${isUser
                                        ? t.avatarUser
                                        : t.avatarBot
                                        }`}>
                                        {isUser ? <User className="w-3.5 h-3.5" /> : (
                                            personaInfo?.avatar ? (
                                                <img src={personaInfo.avatar} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <Bot className="w-3.5 h-3.5" />
                                            )
                                        )}
                                    </div>

                                    {/* Bubble */}
                                    <div className={`rounded-2xl px-4 py-3 ${isUser
                                        ? t.userBubble
                                        : t.bubble
                                        }`}>
                                        <div className={`text-sm leading-relaxed prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-a:underline ${darkMode ? 'prose-invert' : ''} ${t.prose}`}>
                                            <ReactMarkdown
                                                components={{
                                                    a: ({ node, ...props }) => (
                                                        <a {...props} target="_blank" rel="noopener noreferrer" />
                                                    ),
                                                    p: ({ node, ...props }) => (
                                                        <p className="my-1" {...props} />
                                                    ),
                                                }}
                                            >
                                                {msg.content}
                                            </ReactMarkdown>
                                        </div>
                                        {!isUser && renderSpecialContent(msg.special_commands)}
                                        <p className={`text-[10px] mt-1.5 ${isUser ? t.userTime : t.time}`}>
                                            {msg.timestamp?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Typing indicator */}
                    {typing && (
                        <div className="flex justify-start">
                            <div className="flex items-start gap-2.5">
                                <div className={`w-7 h-7 rounded-full ${t.avatarBot} flex items-center justify-center overflow-hidden`}>
                                    {personaInfo?.avatar ? (
                                        <img src={personaInfo.avatar} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <Bot className="w-3.5 h-3.5 text-white" />
                                    )}
                                </div>
                                <div className={`${t.bubble} rounded-2xl px-4 py-3`}>
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input */}
            <div className={`flex-shrink-0 px-4 py-3 ${t.inputBar}`}>
                {error ? (
                    <div className="max-w-3xl mx-auto text-center">
                        <p className="text-sm text-red-400">{error}</p>
                        <button onClick={() => window.location.reload()}
                            className="text-xs text-indigo-400 hover:underline mt-1">Rafraîchir</button>
                    </div>
                ) : (
                    <div className="max-w-3xl mx-auto flex items-center gap-2">
                        <div className="flex-1 relative">
                            <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                rows={1}
                                placeholder={waitingForResponse ? 'Choisissez une option ci-dessus ou tapez votre question...' : (presenting ? 'La présentation est en cours... Tapez pour interrompre' : 'Posez votre question...')}
                                className={`w-full px-4 py-2.5 ${t.input} rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#5764F4]/50 focus:border-transparent resize-none`}
                                style={{ maxHeight: '120px' }}
                                onInput={(e) => {
                                    e.target.style.height = 'auto';
                                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                                }}
                            />
                        </div>
                        <button onClick={sendMessage} disabled={!input.trim() || !connected}
                            className="p-2.5 bg-[#5764F4] hover:bg-[#4654E3] rounded-xl text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0">
                            <Send className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className={`flex-shrink-0 py-2 text-center ${t.inputBar}`}>
                <p className={`text-[10px] ${t.caption}`}>
                    Conçu et développé par <a href="https://abdoulaye-diallo.com" target="_blank" rel="noopener noreferrer" className="hover:underline font-medium">Abdoulaye Diallo</a>
                </p>
            </div>

            {/* Image Modal */}
            {imageModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
                    onClick={() => setImageModal({ open: false, src: '', caption: '' })}>
                    <div className="relative max-w-4xl max-h-[90vh] w-full mx-4" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setImageModal({ open: false, src: '', caption: '' })}
                            className="absolute -top-10 right-0 text-white/80 hover:text-white transition-colors">
                            <X className="w-6 h-6" />
                        </button>
                        <img src={imageModal.src} alt={imageModal.caption}
                            className="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-2xl" />
                        {imageModal.caption && (
                            <p className="text-center text-sm text-gray-300 mt-3">{imageModal.caption}</p>
                        )}
                    </div>
                </div>
            )}

            {/* Video Modal */}
            {showVideoModal && introVideo && (
                <VideoModal
                    introVideo={introVideo}
                    personaInfo={personaInfo}
                    presentationStarted={presentationStarted}
                    onDismiss={() => {
                        setShowVideoModal(false);
                        localStorage.setItem(`talktomyai_video_seen_${slug}`, 'true');
                        if (!wsReady) setWsReady(true);
                    }}
                />
            )}
        </div>
    );
}
